import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    model,
    signal,
    viewChild,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import {
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
    MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { FormsModule } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { DataMartPicklistValue } from '@r1-compass-library/models';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatTooltipModule } from '@angular/material/tooltip';
import { take } from 'rxjs';

interface DialogData {
    badData: string[];
}

export type ChipsBuffetPicklistValue = {
    displayName: string;
    value: string;
};

@Component({
    selector: 'r1-compass-lib-autocomplete-chip-buffet',
    imports: [MatFormFieldModule, MatChipsModule, MatIconModule, MatAutocompleteModule, FormsModule, MatButton],
    templateUrl: './autocomplete-chip-buffet.component.html',
    styles: `
        .chip-list {
            width: 100%;
        }
        #invalid-data-error {
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            button {
                margin-left: 4px;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteChipBuffetComponent {
    readonly #dialog = inject(MatDialog);

    public readonly allEntities = input.required<DataMartPicklistValue[]>();
    public readonly multiPickEntitiesFormCtrl = model<DataMartPicklistValue[]>([]);

    protected entityInput = viewChild.required<ElementRef<HTMLInputElement>>('input');
    protected autocompleteTrigger = viewChild.required(MatAutocompleteTrigger);

    readonly separatorKeysCodes = [ENTER, COMMA] as const;

    protected currentEntity = signal('');

    readonly filteredEntities = computed<DataMartPicklistValue[]>(() => {
        const currentEntity = this.currentEntity().toLowerCase();
        return currentEntity
            ? this.allEntities().filter(entities => entities.displayName.toLowerCase().includes(currentEntity))
            : this.allEntities().slice();
    });

    /**
     * @description A user could paste in 100 CVS values,
     * and as long as they are all valid autocomplete options,
     * we want those. But if any are invalid, we only enter the valid data.
     * For the invalid data in that big list, we trap that invalid data
     * in this variable for their reference in a list of bad data, but we do not
     * actually add that bad data to the input to be saved. They can see one
     * or more items was junk (typo, weird thing to be included, etc) and
     * they are not prevented from saving otherwise valid data.
     */
    protected invalidDataPastedIn = signal<string[]>([]);

    protected openInvalidDataDialog() {
        this.#dialog
            .open<InvalidPasteWarningDialogComponent, DialogData, DialogData>(InvalidPasteWarningDialogComponent, {
                width: '40%',
                height: '40%',
                data: {
                    badData: this.invalidDataPastedIn(),
                },
                disableClose: true,
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(res => {
                this.invalidDataPastedIn.set(res ? [...res.badData] : []);
            });
    }

    protected add(event: MatChipInputEvent): void {
        if (this.autocompleteTrigger().activeOption) {
            return;
        } else {
            const value = (event.value || '').trim();
            const entityDisplayNames = this.allEntities().map(e => e.displayName);

            const updateControlAndErrors = (val: string) => {
                const matchingEntityDisplayNames = (str: string) => this.allEntities().find(e => e.displayName === str);

                if (val && entityDisplayNames.includes(val.trim())) {
                    this.multiPickEntitiesFormCtrl.update(entities => [...entities, matchingEntityDisplayNames(val)!]);
                }
            };

            if (!value) {
                this.currentEntity.set('');
                event.chipInput.clear();
                return;
            }
            if (value.includes(',')) {
                value.split(',').forEach(val => {
                    updateControlAndErrors(val);
                });
            } else {
                updateControlAndErrors(value);
            }
        }

        this.currentEntity.set('');
        event.chipInput.clear();
    }

    protected remove(entity: ChipsBuffetPicklistValue): void {
        this.multiPickEntitiesFormCtrl.update(entities => {
            const index = entities.map(e => e.value).indexOf(entity.value);
            if (index < 0) {
                return entities;
            } else {
                entities.splice(index, 1);
                return [...entities];
            }
        });
    }

    protected selected(event: MatAutocompleteSelectedEvent): void {
        const entity: ChipsBuffetPicklistValue = event.option.value;

        this.multiPickEntitiesFormCtrl.update(entities => [...entities, entity]);
        this.currentEntity.set('');
        event.option.deselect();
        this.entityInput().nativeElement.value = '';
    }

    protected paste(event: ClipboardEvent): void {
        event.preventDefault(); //Prevents the default action
        event.clipboardData
            ?.getData('Text') //Gets the text pasted
            .split(/;|,|\n/) //Splits it when a SEMICOLON or COMMA or NEWLINE
            .forEach(value => {
                const entityDisplayNames = this.allEntities().map(e => e.displayName);
                if (value.trim() && entityDisplayNames.includes(value)) {
                    const matchingEntityDisplayNames = (str: string) =>
                        this.allEntities().find(e => e.displayName === str);
                    this.multiPickEntitiesFormCtrl.update(entities => [
                        ...entities,
                        matchingEntityDisplayNames(value)!,
                    ]);
                } else {
                    this.invalidDataPastedIn.update(entities => [...entities, value]);
                }
            });
    }

    protected displayFn(entity: ChipsBuffetPicklistValue): string {
        return entity?.displayName ? entity.displayName : '';
    }
}

@Component({
    selector: 'r1-compass-lib-invalid-paste-warning',
    template: `
        <h2 mat-dialog-title>Invalid Data</h2>
        <mat-dialog-content>
            <ul>
                @for (badData of data.badData; track $index) {
                    <li>
                        <span class="bad-data">
                            <span>{{ badData }}</span>
                            <button
                                mat-icon-button
                                type="button"
                                color="primary"
                                matTooltip="Delete"
                                aria-label="Delete"
                                (click)="removeBadData(badData)">
                                <fa-icon [icon]="faTrash" />
                            </button>
                        </span>
                    </li>
                }
            </ul>
        </mat-dialog-content>
        <mat-dialog-actions>
            <button mat-flat-button color="primary" (click)="closeDialog()" cdkFocusInitial type="button">
                Cancel
            </button>
        </mat-dialog-actions>
    `,
    styles: `
        mat-dialog-actions {
            display: flex;
            justify-content: flex-end;
        }
        .bad-data {
            display: flex;
            gap: 8px;
            align-items: center;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButtonModule, MatDialogModule, MatIconModule, FontAwesomeModule, MatTooltipModule],
})
export class InvalidPasteWarningDialogComponent {
    dialogRef = inject<MatDialogRef<InvalidPasteWarningDialogComponent>>(MatDialogRef);
    data = inject<DialogData>(MAT_DIALOG_DATA);

    protected readonly faTrash = faTrash;

    constructor() {
        const library = inject(FaIconLibrary);

        library.addIcons(faTrash);
    }

    protected removeBadData(badData: string): void {
        this.data.badData = this.data.badData.filter(d => d !== badData);
    }

    protected closeDialog(): void {
        this.dialogRef.close(this.data);
    }
}
